/** @jsx jsx */
import React, { useState } from 'react';
import { jsx } from '@emotion/core';
import { FormHeader } from './basic/FormHeader';
import { reactivateUser } from '../api';
import { ActionButton } from './basic/ActionButton';
import { useTrackLoadUnload, trackingClient } from '../ext/tracking';
import { Marker } from './meta/Marker';
import { Button } from './magma-wrap/Button';
import { ButtonColor } from 'react-magma-dom';
import { DataInput } from './screens/Profile/data-input/DataInput';
import { isValidStandardInput, ValidatorResponse } from './data-inputs/validated/Validator';
import { Link } from './basic/Links';
import {
  AnalyticsSyntheticValidationError,
  trackErrorEvent
} from '../analytics/errorTypeHelper';

export const TEST_ID = {
  MARKER: 'marker-student-email-sent',
  LOGIN_BUTTON: 'button-login',
  ACTIVATE_BUTTON: 'button-activate',
  ACTIVATE_INPUT: 'input-activate'
};

export interface EmailSentProps {
  emailAddress: string;
}

function trackActivationEmailResendError(error: Error) {
  trackErrorEvent(error, {
    error_type: 'student_activation_resend_email_failed',
    error_description:
      'Student account activation email resend failed.',
    error_location: 'EmailSent.resendActivationEmail'
  });
}

export const EmailSent: React.FunctionComponent<EmailSentProps> = (
  props: EmailSentProps
) => {
  const category = trackingClient.event.registrationActivationSent;
  useTrackLoadUnload(category);
  const [isValidToken, setIsValidToken] = useState(false);
  const [tokenValue, setTokenValue] = useState('');
  const trackActivationCodeValidationError = (error: string) => {
    trackErrorEvent(new AnalyticsSyntheticValidationError(error), {
      error_code: 'STUDENT_ACTIVATION_CODE_VALIDATION_ERROR',
      error_description: `Student activation code validation failed: ${error}`,
      error_location: 'EmailSent.activationCode.onValidationErrorMessageChange'
    });
  };

  return (
    <React.Fragment>
      <Marker testId={TEST_ID.MARKER} />

      <FormHeader>Email sent!</FormHeader>
      <p data-testid="email-instructions">
        An account activation email has been sent to the email address associated with{' '}
        <span style={{ wordWrap: 'break-word' }}>
          {props.emailAddress}
        </span>
        . Use the account activation link or enter the activation code included in the email.
      </p>
      <p>
        If you need help activating your account, contact{' '}
        <Link href={window?.backend?.cengagePhoneHelpUrl}>Support Services</Link>.
      </p>
      <DataInput
        validator={async (value: string) => {
          const isValid: ValidatorResponse = isValidStandardInput(value);
          if (!isValid.response) {
            setIsValidToken(false);
            throw new Error(isValid.reason);
          } else {
            setTokenValue(value);
            setIsValidToken(true);
          }
        }}
        dataLabel={'Activation code'}
        id={'recoveryToken'}
        testId={TEST_ID.ACTIVATE_INPUT}
        onValidationErrorMessageChange={trackActivationCodeValidationError}
      />
      <Button
        onClick={() => {
          (document.location as any) = '/welcome/' + tokenValue +
              '?email=' + props.emailAddress;
        }}
        value="SUBMIT"
        block
        testId={TEST_ID.ACTIVATE_BUTTON}
        disabled={!isValidToken}
      />
      <hr/>
      <ActionButton
        block
        onAction={() => {
          category.resendEmailClick();
          return reactivateUser(props.emailAddress);
        }}
        onFailure={(error: Error) => {
          trackActivationEmailResendError(error);
        }}
        onActionChildren="Sending..."
        toastOnFailure="Email sending error"
        toastOnSuccess="Email sent!"
        testId="resend-email"
        color={ButtonColor.secondary}
      >
        RESEND ACCOUNT ACTIVATION EMAIL
      </ActionButton>
      <Button
        onClick={() => {
          (document.location as any) = '/login' + document.location.search;
        }}
        value="BACK TO SIGN IN"
        color={ButtonColor.subtle}
        block
        testId={TEST_ID.LOGIN_BUTTON}
      />
    </React.Fragment>
  );
};
